import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';
import { BodyText, Icon, theme } from '@sg/garnish';

import { PaymentMethod } from '../../machine/scan-to-pay.machine.types';
import { PAYMENT_METHOD_ICON_MAP } from '../../utils';

export const ScanToPayPaymentMethodOption = (
  props: ScanToPayPaymentMethodOptionProps,
) => {
  const { isSelected, paymentMethod, onSelectPaymentMethod } = props;

  const name = paymentMethod.nickname ?? paymentMethod.description;

  const { formatMessage } = useIntl();
  const a11yLabel = formatMessage(messages.a11y, { name });

  const handleOnPress = useCallback(() => {
    onSelectPaymentMethod(paymentMethod.id);
  }, [onSelectPaymentMethod, paymentMethod]);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
      style={[styles.cta, isSelected && styles.ctaSelected]}
      onPress={handleOnPress}
    >
      <View style={styles.leftSide}>
        <Icon
          name={PAYMENT_METHOD_ICON_MAP[paymentMethod.cardType]}
          width={32}
          height={32}
        />

        <BodyText sizeMatch={['24']}>{name}</BodyText>
      </View>

      {isSelected ? (
        <Icon
          name="IconCheck"
          width={24}
          height={24}
          color={theme.colors.KALE}
        />
      ) : null}
    </Pressable>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11y: {
    defaultMessage: 'Select payment method {name}',
    description: 'Scan to Pay | Payment Method Option | A11y',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 40,
    borderRadius: 40,
    gap: theme.spacing['3'],
    paddingHorizontal: theme.spacing['4'],
    marginHorizontal: theme.spacing['4'],
  },
  ctaSelected: {
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
  leftSide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['3'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayPaymentMethodOptionProps = {
  isSelected: boolean;
  paymentMethod: PaymentMethod;
  onSelectPaymentMethod: (paymentMethodId: string) => void;
};
